"use client"

import posthog from "posthog-js"

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST

let initialized = false

export function isAnalyticsEnabled(): boolean {
  return Boolean(POSTHOG_KEY && POSTHOG_HOST)
}

export function initAnalytics(): void {
  if (initialized) return
  if (typeof window === "undefined") return
  if (!POSTHOG_KEY || !POSTHOG_HOST) return
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    capture_pageview: true,
    // Client-side error tracking: autocapture uncaught exceptions and
    // unhandled promise rejections as $exception events. console.error is
    // left off deliberately to avoid noisy, high-volume ingestion.
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
    },
  })
  initialized = true
}

interface IdentifyTraits {
  email: string
  tenant_id: number | null
  tenant_name: string
  role: string
}

export function identifyUser(userId: string | number, traits: IdentifyTraits): void {
  if (!isAnalyticsEnabled() || !initialized) return
  posthog.identify(String(userId), traits)
}

export function resetAnalytics(): void {
  if (!isAnalyticsEnabled() || !initialized) return
  posthog.reset()
}

export function captureEvent(event: string, properties?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled() || !initialized) return
  posthog.capture(event, properties)
}

/**
 * Manually report a handled error to PostHog Error Tracking. Use inside React
 * error boundaries (app/error.tsx, app/global-error.tsx) and try/catch sites
 * where the error is swallowed before window.onerror autocapture can see it.
 */
export function captureException(error: unknown, properties?: Record<string, unknown>): void {
  if (!isAnalyticsEnabled() || !initialized) return
  posthog.captureException(error, properties)
}
